import { useMemo, useContext } from 'react'

import { useSelector } from 'react-redux'
import { faGraduationCap } from '@fortawesome/pro-solid-svg-icons'

import { Action } from '../../../Components/antd/Tables/DataTable/utils/actions'
import DataTable from '../../../Components/antd/Tables/DataTable/DataTable'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { ContactPersonDrawerContext } from '../../../Providers/Drawer/ContactPerson/ContactPersonDrawer'
import { OrderBy } from '../../../utils/constants'
import { getEmail, getFirstname, getInstitutionsName, getLastname, getPhone, getRole, getSchoolName } from '../../../utils/entities/user'
import { Filter } from '../../../Components/antd/Tables/DataTable/utils/tableFilter'
import SpecialitiesSelector from '../../../Components/shared/Selectors/SpecialitiesSelector'

import '../../../assets/contact-persons-view.scss'
import VisibilityValues from '../../shared/ContactPersons/VisibilityValues'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { isInstitutionMaccsUser, isSchoolMaccsUser } from '../../../utils/roles'
import InstitutionsSelector from '../../../Components/shared/Selectors/Institution/InstitutionsSelector'
import SchoolsSelector from '../../../Components/shared/Selectors/School/SchoolsSelector'

const ContactPersonsTable = ({ data, filters = {}, loading, metadata, tableParameters, visibility, onFiltersChange, onTableParametersChange }) => {
  const { user } = useContext(GlobalContext)
  const { setSelectedUser } = useContext(ContactPersonDrawerContext)

  const t = useSelector(selectTranslate)

  const columns = useMemo(() => {
    let columns = [
      { title: t('common.lastname'), orderBy: OrderBy.LASTNAME, key: getLastname },
      { title: t('common.firstname'), orderBy: OrderBy.FIRSTNAME, key: getFirstname },
      { title: t('common.email'), orderBy: OrderBy.EMAIL, key: getEmail },
      { title: t('common.phone_number'), key: getPhone }
    ]
    if (visibility === VisibilityValues.EXTERNAL) {
      if (isSchoolMaccsUser(user)) {
        columns = [
          { title: t('common.institution'), key: getInstitutionsName },
          { title: t('common.role'), key: u => t(`roles.${getRole(u).toLowerCase()}`) },
          { title: t('common.lastname'), orderBy: OrderBy.LASTNAME, key: getLastname },
          { title: t('common.firstname'), orderBy: OrderBy.FIRSTNAME, key: getFirstname },
          { title: t('common.email'), orderBy: OrderBy.EMAIL, key: getEmail },
          { title: t('common.phone_number'), key: getPhone }
        ]
      } else if (isInstitutionMaccsUser(user)) {
        columns = [
          { title: t('common.school'), orderBy: OrderBy.SCHOOL, key: getSchoolName },
          { title: t('common.role'), key: u => t(`roles.${getRole(u).toLowerCase()}`) },
          { title: t('common.lastname'), orderBy: OrderBy.LASTNAME, key: getLastname },
          { title: t('common.firstname'), orderBy: OrderBy.FIRSTNAME, key: getFirstname },
          { title: t('common.email'), orderBy: OrderBy.EMAIL, key: getEmail },
          { title: t('common.phone_number'), key: getPhone }
        ]
      }
    }

    return columns
  }, [visibility, t])

  const tableActions = useMemo(() => [
    new Action({
      icon: faGraduationCap,
      title: t('contact_persons_view.assignments_button_title'),
      key: t('contact_persons_table.display_contact_person_specialities'),
      onClick: setSelectedUser
    })
  ], [t, setSelectedUser])

  const localFilters = useMemo(() => {
    const localFilters = [
      new Filter({
        key: 'specialities',
        label: t('common.specialities'),
        component: SpecialitiesSelector,
        props: {
          multiple: 'multiple',
          values: filters.specialities,
          onChange: s => onFiltersChange(s, 'specialities'),
          style: { minWidth: '210px', maxWidth: '420px' }
        }
      })
    ]

    if (visibility === VisibilityValues.EXTERNAL) {
      if (isSchoolMaccsUser(user)) {
        localFilters.push(new Filter({
          key: 'institutions',
          label: t('common.institutions'),
          component: InstitutionsSelector,
          props: {
            multiple: 'multiple',
            values: filters.institutions,
            onChange: s => onFiltersChange(s, 'institutions'),
            style: { minWidth: '210px', maxWidth: '420px' }
          }
        }))
      } else if (isInstitutionMaccsUser(user)) {
        localFilters.push(new Filter({
          key: 'schools',
          label: t('common.schools'),
          component: SchoolsSelector,
          props: {
            multiple: 'multiple',
            values: filters.schools,
            onChange: s => onFiltersChange(s, 'schools'),
            style: { minWidth: '210px', maxWidth: '420px' }
          }
        }))
      }
    }

    return localFilters
  }, [filters, onFiltersChange, user, t])

  return (
    <DataTable
      columns={columns}
      data={data}
      metadata={metadata}
      loading={loading}
      parameters={tableParameters}
      onParametersChange={onTableParametersChange}
      actions={tableActions}
      formatColumns={columns}
      filters={localFilters}
    />
  )
}

export default ContactPersonsTable
